import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    getSidePanelLayersPlugins,
    getSidePanelLayerTypeToUpdateFunctionMap,
} from "plugins/plugin-registry"
import { compareFunctionsBySortMode } from "./constants"
import { selectEntitiesFromIds } from "./slice"
import {
    selectFilterLayersCreatorId,
    selectFilterLayersValue,
    selectSearchLayersValue,
    selectSortLayersValue,
} from "./slice.new"
import { useMemo } from "react"
import { omit } from "nodash"
import { SidePanelLayerType } from "@nova/constants"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"

export type SidePanelLayersData = {
    id: string
    type: SidePanelLayerType
    index: string
    parentId?: string
    archivedTimestamp?: number
    children: SidePanelLayersData[]
    plugin: ReturnType<typeof getSidePanelLayersPlugins>[number]
}

export type FlattenedSidePanelLayersData = Omit<SidePanelLayersData, "children"> & {
    depth: number
    isLastItemInParent: boolean
    directChildrenCount: number
    ancestors: { id: string; isLastItemInParent: boolean }[]
}

const useLayerSidePanelData = () => {
    const dispatch = useAppDispatch()
    const sidePanelPlugins = getSidePanelLayersPlugins()
    const data = sidePanelPlugins.reduce(
        (acc, cur) => {
            const entities = cur.useSidePanelEntities()
            entities
                .filter((entity) => !entity.archivedTimestamp)
                .forEach((entity) => {
                    acc[entity.id] = {
                        ...entity,
                        type: cur.sidePanelLayerType,
                        children: [],
                        plugin: cur,
                    }
                })
            return acc
        },
        {} as Record<string, SidePanelLayersData>
    )
    const roots: SidePanelLayersData[] = []
    Object.values(data).forEach((entity) => {
        if (entity.parentId && entity.parentId !== "null") {
            const parent = data[entity.parentId]
            if (parent) {
                parent.children.push(entity)
            } else {
                dispatch(
                    getSidePanelLayerTypeToUpdateFunctionMap()[entity.type]({
                        id: entity.id,
                        updates: {
                            parentId: "null",
                        },
                    })
                )
            }
        } else {
            roots.push(entity)
        }
    })
    return roots
}

const useLayerSidePanelEntityMap = () => {
    const sidePanelData = useLayerSidePanelData()
    const entities = useAppSelector(
        selectEntitiesFromIds(
            sidePanelData.reduce((acc, cur) => {
                const pushNestedIds = (data: SidePanelLayersData[]) => {
                    data.forEach((d) => {
                        acc.push(d.id)
                        pushNestedIds(d.children)
                    })
                }
                pushNestedIds([cur])
                return acc
            }, [] as string[])
        )
    )
    const entityMap = useMemo(() => {
        return Object.fromEntries(entities.map((entity) => [entity.id, entity]))
    }, [entities])
    return entityMap
}

const useFilteredAndSortedLayerSidePanelData = () => {
    const sidePanelData = useLayerSidePanelData()
    const searchLayersValue = useAppSelector(selectSearchLayersValue)
    const filterLayersValue = useAppSelector(selectFilterLayersValue)
    const filterLayerCreatorId = useAppSelector(selectFilterLayersCreatorId)
    const sortLayersValue = useAppSelector(selectSortLayersValue)
    const entityMap = useLayerSidePanelEntityMap()
    const filterList = (list: SidePanelLayersData[]) => {
        return list
            .map((data): SidePanelLayersData | null => {
                const entity = entityMap[data.id]
                const filteredChildren = data.children ? filterList(data.children) : []
                const matchesSearch = entity.name
                    .toLowerCase()
                    .includes(searchLayersValue.toLocaleLowerCase())
                const matchesFilter =
                    !filterLayersValue.length ||
                    !data.plugin.filterKey ||
                    filterLayersValue.includes(data.plugin.filterKey)
                const matchesCreator =
                    !filterLayerCreatorId || entity.createdBy?.userId === filterLayerCreatorId
                const matches = matchesSearch && matchesFilter && matchesCreator
                if (matches || filteredChildren.length > 0) {
                    return { ...data, children: filteredChildren }
                }
                return null
            })
            .filter((x): x is SidePanelLayersData => x !== null)
    }
    const filteredList = filterList(sidePanelData)
    const compareFn = compareFunctionsBySortMode[sortLayersValue]
    const sortArr = (arr: SidePanelLayersData[]) => {
        arr.sort((a, b) => {
            const entityA = entityMap[a.id]
            const entityB = entityMap[b.id]
            return compareFn(entityA, entityB)
        })
        arr.forEach((item) => {
            if (item.children) sortArr(item.children)
        })
    }
    sortArr(filteredList)
    return filteredList
}

export const useFlattenedFilteredAndSortedLayerSidePanelData = () => {
    const layers = useFilteredAndSortedLayerSidePanelData()
    const expandedLayerIds = useLocalStorageValue("sidePanelExpandedLayerIds")
    const flattenedLayers = useMemo(() => {
        const result = [] as FlattenedSidePanelLayersData[]
        const stack = [] as ({
            layer: SidePanelLayersData
        } & Pick<FlattenedSidePanelLayersData, "depth" | "ancestors" | "isLastItemInParent">)[]
        for (let i = layers.length - 1; i >= 0; i--) {
            stack.push({
                layer: layers[i],
                depth: 0,
                ancestors: [],
                isLastItemInParent: i === layers.length - 1,
            })
        }
        while (stack.length > 0) {
            const { layer, depth, ancestors, isLastItemInParent } = stack.pop()!
            result.push({
                ...omit(layer, ["children"]),
                depth,
                isLastItemInParent,
                directChildrenCount: layer.children.length,
                ancestors,
            })
            if (layer.children.length) {
                if (expandedLayerIds.includes(layer.id)) {
                    for (let i = layer.children.length - 1; i >= 0; i--) {
                        stack.push({
                            layer: layer.children[i],
                            depth: depth + 1,
                            ancestors: [
                                ...ancestors,
                                {
                                    id: layer.id,
                                    isLastItemInParent,
                                },
                            ],
                            isLastItemInParent: i === layer.children.length - 1,
                        })
                    }
                }
            }
        }
        return result
    }, [expandedLayerIds, layers])
    return flattenedLayers
}

export const useLayerSidePanelSortedEntities = () => {
    const layerSidePanelData = useFilteredAndSortedLayerSidePanelData()
    const entityMap = useLayerSidePanelEntityMap()
    const getEntity = (sidePanelData: SidePanelLayersData) => {
        const entity = entityMap[sidePanelData.id]
        entity.children = sidePanelData.children.map((child) => getEntity(child))
        return entity
    }
    return layerSidePanelData.map((layer) => getEntity(layer))
}

export const useLayerFilterOptionCounts = () => {
    const layerSidePanelData = useLayerSidePanelData()
    const counts = {} as Record<string, number>
    const updateCounts = (data: SidePanelLayersData) => {
        if (data.plugin.filterKey) {
            if (!counts[data.plugin.filterKey]) counts[data.plugin.filterKey] = 0
            counts[data.plugin.filterKey] += 1
        }
        if (data.children.length) {
            data.children.forEach((child) => updateCounts(child))
        }
    }
    layerSidePanelData.forEach((data) => {
        updateCounts(data)
    })
    return counts
}
